import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  Search,
  FolderOpen,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  Music2,
  TrendingUp,
  Loader2,
  Trash2,
  Database,
  Gauge,
} from "lucide-react";
import axios from "axios";
import { useQuota } from "../contexts/QuotaContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  failed: "bg-red-500/10 text-red-400 border-red-500/20",
  searching: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  queued: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  partial: "bg-purple-500/10 text-purple-400 border-purple-500/20",
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const { unlimited, used, limit: quotaLimit, remaining } = useQuota();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSearch = async (searchId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDeleting(searchId);
    try {
      await axios.delete(`${API}/search/${searchId}`);
      toast.success("Search archived — data retained");
      fetchStats();
    } catch (error) {
      toast.error("Failed to archive search");
    } finally {
      setDeleting(null);
    }
  };

  const handleClearAllSearches = async () => {
    if (!window.confirm("Archive all searches? Data will be retained and can be restored from the Archived tab.")) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.delete(`${API}/searches`);
      toast.success(`Archived ${response.data.archived_count} searches`);
      fetchStats();
    } catch (error) {
      toast.error("Failed to archive searches");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-syncchain-accent" />
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Searches",
      value: stats?.total_searches || 0,
      icon: Search,
      color: "text-syncchain-accent",
      bgColor: "bg-syncchain-accent/10",
    },
    {
      title: "Completed",
      value: stats?.completed_searches || 0,
      icon: CheckCircle2,
      color: "text-syncchain-success",
      bgColor: "bg-syncchain-success/10",
    },
    {
      title: "Failed",
      value: stats?.failed_searches || 0,
      icon: XCircle,
      color: "text-syncchain-error",
      bgColor: "bg-syncchain-error/10",
    },
    {
      title: "Batch CSV",
      value: stats?.total_batches || 0,
      icon: FolderOpen,
      color: "text-syncchain-info",
      bgColor: "bg-syncchain-info/10",
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-syncchain-text">
            Dashboard
          </h1>
          <p className="text-syncchain-text-secondary mt-1">
            Rights & metadata resolution overview
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            asChild
            className="bg-syncchain-accent hover:bg-syncchain-accent-hover text-white rounded-full px-6 glow-hover"
            data-testid="new-search-btn"
          >
            <Link to="/search">
              <Search className="w-4 h-4 mr-2" />
              Single Song Search
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-white/10 text-syncchain-text hover:bg-white/5 rounded-full px-6"
            data-testid="view-batches-btn"
          >
            <Link to="/batches">
              <FolderOpen className="w-4 h-4 mr-2" />
              Batch CSV
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card
            key={stat.title}
            className="bg-syncchain-paper border-white/10 hover:border-syncchain-accent/30 transition-colors group"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-1">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-heading font-bold text-syncchain-text">
                    {stat.value.toLocaleString()}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quota Usage */}
      {!unlimited && (
        <Card className="bg-syncchain-paper border-white/10" data-testid="dashboard-quota-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Gauge className="w-5 h-5 text-syncchain-accent" />
              <h3 className="font-heading font-semibold text-syncchain-text">Your Usage</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { label: "Single Searches", key: "single_search" },
                { label: "Catalogue Imports", key: "catalogue_import" },
                { label: "Batch CSV Uploads", key: "batch_csv" },
              ].map(({ label, key }) => {
                const u = used(key);
                const l = quotaLimit(key);
                const pct = l > 0 ? Math.min(100, (u / l) * 100) : 0;
                const exhausted = u >= l;
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-syncchain-text-secondary">{label}</span>
                      <span className={`font-mono text-xs ${exhausted ? "text-red-400" : "text-syncchain-text"}`}>
                        {u} / {l}
                      </span>
                    </div>
                    <Progress
                      value={pct}
                      className={`h-2 ${exhausted ? "[&>div]:bg-red-500" : "[&>div]:bg-syncchain-accent"}`}
                    />
                    <p className="text-xs text-syncchain-text-muted">
                      {exhausted ? "Limit reached" : `${remaining(key)} remaining`}
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Searches */}
      <Card className="bg-syncchain-paper border-white/10">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="font-heading text-xl text-syncchain-text flex items-center gap-2">
            <Clock className="w-5 h-5 text-syncchain-text-secondary" />
            Recent Searches
            <span className="text-sm font-normal text-syncchain-text-muted">
              (showing 5 of {stats?.total_searches || 0})
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {stats?.total_searches > 5 && (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-white/10 text-syncchain-text-secondary hover:bg-white/5"
              >
                <Link to="/searches">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            )}
            {stats?.total_searches > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-syncchain-error/70 hover:text-syncchain-error hover:bg-syncchain-error/10"
                onClick={handleClearAllSearches}
                data-testid="clear-all-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {stats?.recent_searches?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_searches.map((search) => (
                <div
                  key={search.id}
                  className="flex items-center gap-4 p-4 rounded-lg bg-syncchain-surface/50 border border-white/5 hover:border-syncchain-accent/30 transition-all group"
                  data-testid={`recent-search-${search.id}`}
                >
                  <Link
                    to={`/search/${search.id}`}
                    className="flex items-center gap-4 flex-1 min-w-0"
                  >
                    <div className="w-10 h-10 rounded-lg bg-syncchain-surface flex items-center justify-center shrink-0 overflow-hidden">
                      {search.results_json?.recording?.imageUrl ? (
                        <img 
                          src={search.results_json.recording.imageUrl} 
                          alt={`${search.track_title || 'Track'} artwork`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Music2 className="w-5 h-5 text-syncchain-text-secondary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-syncchain-text truncate">
                        {search.track_title || search.query_title || search.query_isrc || "Untitled"}
                      </p>
                      <p className="text-sm text-syncchain-text-secondary truncate">
                        {search.track_artist || search.query_artist || "Unknown Artist"}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={`${statusColors[search.status]} text-xs font-mono uppercase shrink-0`}
                    >
                      {search.status}
                    </Badge>
                    <ArrowRight className="w-4 h-4 text-syncchain-text-muted group-hover:text-syncchain-accent transition-colors shrink-0" />
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-syncchain-text-muted hover:text-syncchain-error hover:bg-syncchain-error/10 shrink-0"
                    onClick={(e) => handleDeleteSearch(search.id, e)}
                    disabled={deleting === search.id}
                    data-testid={`delete-search-${search.id}`}
                  >
                    {deleting === search.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Database className="w-12 h-12 text-syncchain-text-muted mx-auto mb-4" />
              <p className="text-syncchain-text-secondary">No searches yet</p>
              <Button
                asChild
                variant="link"
                className="text-syncchain-accent mt-2"
              >
                <Link to="/search">Create your first search</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-4">
        <Card className="bg-syncchain-paper border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-syncchain-accent/20 to-syncchain-accent/5 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-syncchain-accent" />
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">
                  Success Rate
                </p>
                <p className="text-2xl font-heading font-bold text-syncchain-text">
                  {stats?.total_searches > 0
                    ? Math.round((stats.completed_searches / stats.total_searches) * 100)
                    : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
